import React, { useEffect, useState } from "react";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import { ReactComponent as ExpandAll } from "images/expand_all.svg";
import { ReactComponent as CollapseAll } from "images/collapse-all.svg";
import './filters.scss'
import { useStaticRoutes } from "../hooks/useStaticRoutes";
import { useDispatch, useSelector } from "react-redux";
import { userSelector } from "slices/user/userSlice";
import { setStaticRoutes } from "slices/pickup/unassignedPickupSlice";
import { normalizeUnassignedGroupedApiResponse } from "../unassignedGroupedApiNormalize";

interface FilterComponentProps {
  primaryFilterPlaceholder: string;
  secondaryFilterPlaceholder: string;
  selectedTab: string;
  /** When false, expand/collapse controls are not shown (e.g. rendered in column header). */
  showExpandCollapse?: boolean;
  handleExpand?: () => void;
  handleCollapse?: () => void;
  shouldFetchStaticRoutes?: boolean;
  /** When set with onPrimaryFilterChange, primary input is controlled (e.g. drivers column client filter). */
  primaryFilterValue?: string;
  onPrimaryFilterChange?: (value: string) => void;
  secondaryFilterValue?: string;
  onSecondaryFilterChange?: (value: string) => void;
}

export const FilterComponent: React.FC<FilterComponentProps> = ({
  primaryFilterPlaceholder,
  secondaryFilterPlaceholder,
  selectedTab,
  showExpandCollapse = true,
  handleExpand,
  handleCollapse,
  shouldFetchStaticRoutes = true,
  primaryFilterValue,
  onPrimaryFilterChange,
  secondaryFilterValue,
  onSecondaryFilterChange,
}) => {
  const dispatch = useDispatch();
  const userState = useSelector(userSelector);

  const [internalPrimary, setInternalPrimary] = useState<string>("");
  const [internalSecondary, setInternalSecondary] = useState<string>("");

  const primaryControlled = onPrimaryFilterChange != null;
  const secondaryControlled = onSecondaryFilterChange != null;
  const primarySearch = primaryControlled ? (primaryFilterValue ?? "") : internalPrimary;
  const secondarySearch = secondaryControlled ? (secondaryFilterValue ?? "") : internalSecondary;

  const setPrimarySearch = (v: string) => {
    if (!primaryControlled) setInternalPrimary(v);
    onPrimaryFilterChange?.(v);
  };
  const setSecondarySearch = (v: string) => {
    if (!secondaryControlled) setInternalSecondary(v);
    onSecondaryFilterChange?.(v);
  };

  const routes = useStaticRoutes(
    userState.activeTerminal?.id,
    primarySearch,
    secondarySearch,
    selectedTab,
    shouldFetchStaticRoutes
  )

  useEffect(() => {
    if (shouldFetchStaticRoutes && routes?.routeDetails?.data) {
      const terminalId = userState.activeTerminal?.id;
      dispatch(
        setStaticRoutes({
          data: normalizeUnassignedGroupedApiResponse(routes.routeDetails.data, terminalId),
        })
      );
    }
  }, [routes?.routeDetails?.data, dispatch, shouldFetchStaticRoutes, userState.activeTerminal?.id]);

  return (
    <div className="xgs-pickup-assignment-filters">
      <div className="xgs-pickup-assignment-filter-inputs">
        <LabeledTextInput
          name="searchString"
          labelMode={LabelModes.column}
          className="xgs-pickup-assignment-filter-input"
          type="text"
          value={primarySearch}
          onKeyDown={(e) => (e.key === "Enter")}
          onChange={(e) => setPrimarySearch(e.currentTarget.value)}
          placeholder={primaryFilterPlaceholder}
          clearable
          onClear={() => setPrimarySearch("")}
        />
        <LabeledTextInput
          name="searchString"
          labelMode={LabelModes.column}
          className="xgs-pickup-assignment-filter-input"
          type="text"
          value={secondarySearch}
          onKeyDown={(e) => (e.key === "Enter")}
          onChange={(e) => setSecondarySearch(e.currentTarget.value)}
          placeholder={secondaryFilterPlaceholder}
          clearable
          onClear={() => setSecondarySearch("")}
        />      </div>

      {showExpandCollapse && handleExpand && handleCollapse && (
        <div className="xgs-pickup-assignment-filter-actions">
          <div onClick={handleExpand}>
            <ExpandAll color="#2F80ED" /> Expand All
          </div>
          <div onClick={handleCollapse}>
            <CollapseAll color="#2F80ED" /> Collapse All
          </div>
        </div>
      )}
    </div>
  );
};
